import {
	type UseMutationResult,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import { queryKeys } from "@/api/query-keys";
import type { AssignSourceToDistrictRequest } from "@/api/reseller/types";
import { api } from "@/lib/api";
import { useRateLimitedMutation } from "@/lib/use-rate-limited-mutation";
import type {
	ApartmentCreateRequest,
	ApartmentUpdateRequest,
	MeterKeyCreateRequest,
	UpdateDistrictManagerProfileRequest,
	UpdateMeterAddressRequest,
	UploadApartmentsRequest,
	UploadMetersRequest,
	UploadMetersResponse,
} from "./types";

const CRUD_RATE_LIMIT = { limit: 10, windowMs: 60 * 1000 } as const;

export function useUpdateMeterAddress(
	serialNumber: string,
): UseMutationResult<unknown, Error, UpdateMeterAddressRequest> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (data: UpdateMeterAddressRequest) => {
			const res = await api.patch(
				`/api/districts/meter/${serialNumber}/`,
				data,
			);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.districts.all });
		},
	});
}

export function useAssignMeterToApartment(): UseMutationResult<
	unknown,
	Error,
	{ serialNumber: string; apartment: number }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({
			serialNumber,
			apartment,
		}: {
			serialNumber: string;
			apartment: number;
		}) => {
			const res = await api.patch(`/api/districts/meter/${serialNumber}/`, {
				apartment,
			});
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.districts.all });
		},
	});
}

export function useUnassignMeterFromApartment(): UseMutationResult<
	unknown,
	Error,
	{ serialNumber: string }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ serialNumber }: { serialNumber: string }) => {
			const res = await api.patch(`/api/districts/meter/${serialNumber}/`, {
				apartment: null,
			});
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.districts.all });
		},
	});
}

export function useUpdateMeterEncryptionKey(): UseMutationResult<
	unknown,
	Error,
	{ serialNumber: string; encryptionKey: string }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({
			serialNumber,
			encryptionKey,
		}: {
			serialNumber: string;
			encryptionKey: string;
		}) => {
			const res = await api.patch(`/api/districts/meter/${serialNumber}/`, {
				encryption_key: encryptionKey,
			});
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.districts.all });
		},
	});
}

export function useCreateMeterKey(): UseMutationResult<
	unknown,
	Error,
	MeterKeyCreateRequest
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (data: MeterKeyCreateRequest) => {
			const res = await api.post("/api/districts/meter_keys/", data);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.districts.meterKeys() });
		},
	});
}

export function useCreateApartment() {
	const qc = useQueryClient();
	return useRateLimitedMutation(
		{
			mutationFn: async (data: ApartmentCreateRequest) => {
				const res = await api.post("/api/districts/apartments/", data);
				return res.data;
			},
			onSuccess: () => {
				qc.invalidateQueries({ queryKey: queryKeys.districts.all });
			},
		},
		{ key: "districts:create-apartment", ...CRUD_RATE_LIMIT },
	);
}

const MAX_CSV_SIZE = 10 * 1024 * 1024;

export function useUploadApartments(): UseMutationResult<
	unknown,
	Error,
	UploadApartmentsRequest
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ district_id, file }: UploadApartmentsRequest) => {
			if (file.size > MAX_CSV_SIZE) {
				throw new Error("Plik jest za duży. Maksymalny rozmiar to 10 MB.");
			}
			const formData = new FormData();
			formData.append("district_id", String(district_id));
			formData.append("file", file);
			const res = await api.post(
				"/api/districts/apartments/upload_csv/",
				formData,
				{ headers: { "Content-Type": "multipart/form-data" } },
			);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.districts.all });
		},
	});
}

export function useUploadMeters(): UseMutationResult<
	UploadMetersResponse,
	Error,
	UploadMetersRequest
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ district_id, file }: UploadMetersRequest) => {
			if (file.size > MAX_CSV_SIZE) {
				throw new Error("Plik jest za duży. Maksymalny rozmiar to 10 MB.");
			}
			const formData = new FormData();
			formData.append("district_id", String(district_id));
			formData.append("file", file);
			const res = await api.post<UploadMetersResponse>(
				"/api/districts/meters/upload_csv/",
				formData,
				{ headers: { "Content-Type": "multipart/form-data" } },
			);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.districts.all });
		},
	});
}

export function useUpdateApartment(
	pk: number,
): UseMutationResult<unknown, Error, ApartmentUpdateRequest> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (data: ApartmentUpdateRequest) => {
			const res = await api.patch(`/api/districts/apartments/${pk}/`, data);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.districts.apartment(pk) });
			qc.invalidateQueries({ queryKey: queryKeys.districts.all });
		},
	});
}

export function useDeleteApartment(): UseMutationResult<void, Error, number> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (pk: number) => {
			await api.delete(`/api/districts/apartments/${pk}/`);
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.districts.all });
		},
	});
}

export function useRenameDistrict(
	id: number,
): UseMutationResult<unknown, Error, { name: string }> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (data: { name: string }) => {
			const res = await api.patch(`/api/districts/${id}/`, data);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.districts.all });
		},
	});
}

export function useDeleteDistrict(): UseMutationResult<void, Error, number> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (id: number) => {
			await api.delete(`/api/districts/${id}/`);
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.districts.all });
		},
	});
}

export function useCreateDistrictDM() {
	const qc = useQueryClient();
	return useRateLimitedMutation(
		{
			mutationFn: async (data: { name: string }) => {
				const res = await api.post("/api/districts/", data);
				return res.data;
			},
			onSuccess: () => {
				qc.invalidateQueries({ queryKey: queryKeys.districts.all });
			},
		},
		{ key: "districts:create-district-dm", ...CRUD_RATE_LIMIT },
	);
}

export function useAssignSourceToDistrict(): UseMutationResult<
	unknown,
	Error,
	AssignSourceToDistrictRequest
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (data: AssignSourceToDistrictRequest) => {
			const res = await api.post(
				"/api/districts/assign_source_to_district/",
				data,
			);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.districts.all });
		},
	});
}

export function useUpdateDistrictManagerProfile(): UseMutationResult<
	unknown,
	Error,
	UpdateDistrictManagerProfileRequest
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (data: UpdateDistrictManagerProfileRequest) => {
			const res = await api.patch("/api/districts/my-profile/", data);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.districts.myProfile() });
		},
	});
}
